import React, { useMemo, useState, useEffect } from "react"
import { Link } from "react-router-dom"
import SEO from "../../components/SEO.jsx"
import { LANDING_PAGES } from "./landingPages.js"

const STORAGE_KEY = "enabledLandingPages"

function getEnabledLandingKeys() {
  try {
    if (typeof window === "undefined") return []
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function setEnabledLandingKeys(keys) {
  try {
    if (typeof window === "undefined") return
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(keys))
  } catch {
    // ignore
  }
}

function groupForPath(path = "") {
  const p = String(path || "").toLowerCase()
  if (p.includes("/websites-for-")) return "Business segments"
  if (p.includes("google") || p.includes("maps") || p.includes("search-console"))
    return "Google visibility"
  if (p.includes("speed") || p.includes("pagespeed")) return "Speed and performance"
  if (p.includes("form") || p.includes("enquir") || p.includes("contact"))
    return "Forms and enquiries"
  if (p.includes("pay-") || p.includes("no-payment") || p.includes("build-now"))
    return "Payment options"
  return "Problem solving"
}

function safeKey(p, idx) {
  return p?.key || p?.path || `${p?.title || "lp"}-${idx}`
}

export default function LandingIndex() {
  const [q, setQ] = useState("")
  const [enabledKeys, setEnabledKeys] = useState([])
  const [copiedKey, setCopiedKey] = useState(null)

  useEffect(() => {
    if (typeof window === "undefined") return
    setEnabledKeys(getEnabledLandingKeys())

    const onStorage = (e) => {
      if (!e || e.key === STORAGE_KEY) setEnabledKeys(getEnabledLandingKeys())
    }
    window.addEventListener("storage", onStorage)
    return () => window.removeEventListener("storage", onStorage)
  }, [])

  const items = useMemo(() => {
    const needle = q.trim().toLowerCase()

    const filtered = LANDING_PAGES.filter((p) => {
      if (!needle) return true
      return (
        (p.key || "").toLowerCase().includes(needle) ||
        (p.path || "").toLowerCase().includes(needle) ||
        (p.heroHeading || "").toLowerCase().includes(needle) ||
        (p.title || "").toLowerCase().includes(needle)
      )
    })

    const withGroup = filtered.map((p, idx) => ({
      ...p,
      _safeKey: safeKey(p, idx),
      group: groupForPath(p.path),
    }))

    withGroup.sort(
      (a, b) =>
        (a.group || "").localeCompare(b.group || "") ||
        (a.path || "").localeCompare(b.path || "")
    )

    return withGroup
  }, [q])

  const origin = typeof window !== "undefined" ? window.location.origin : ""

  const isLive = (key) => enabledKeys.includes(key)

  const toggleLive = (key) => {
    if (!key) return
    const next = isLive(key) ? enabledKeys.filter((k) => k !== key) : [...enabledKeys, key]
    setEnabledLandingKeys(next)
    setEnabledKeys(next)
  }

  const enableFiltered = () => {
    const keys = items.map((p) => p.key).filter(Boolean)
    const merged = Array.from(new Set([...enabledKeys, ...keys]))
    setEnabledLandingKeys(merged)
    setEnabledKeys(merged)
  }

  const disableFiltered = () => {
    const keys = new Set(items.map((p) => p.key).filter(Boolean))
    const next = enabledKeys.filter((k) => !keys.has(k))
    setEnabledLandingKeys(next)
    setEnabledKeys(next)
  }

  const enableTop10 = () => {
    const keys = items.slice(0, 10).map((p) => p.key).filter(Boolean)
    const merged = Array.from(new Set([...enabledKeys, ...keys]))
    setEnabledLandingKeys(merged)
    setEnabledKeys(merged)
  }

  const disableAll = () => {
    setEnabledLandingKeys([])
    setEnabledKeys([])
  }

  const copyUrl = async (key, path) => {
    if (typeof window === "undefined") return
    const url = `${window.location.origin}${path || ""}`

    try {
      await navigator.clipboard.writeText(url)
    } catch {
      try {
        const ta = document.createElement("textarea")
        ta.value = url
        ta.setAttribute("readonly", "")
        ta.style.position = "absolute"
        ta.style.left = "-9999px"
        document.body.appendChild(ta)
        ta.select()
        document.execCommand("copy")
        document.body.removeChild(ta)
      } catch {
        // ignore
      }
    }

    setCopiedKey(key)
    window.setTimeout(() => setCopiedKey(null), 1200)
  }

  return (
    <div className="bg-white text-gray-900">
      <SEO
        title="Landing Pages Index | Private | 2merative"
        description="Private index of landing pages."
        path="/lp-index-72a9"
        image="/og-image.png"
      />

      <main className="pt-28 sm:pt-32 pb-20 px-4">
        <section className="max-w-5xl mx-auto mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-blue-900 mb-3">
            Landing Pages Index
          </h1>
          <p className="text-gray-700">
            Private hub for reviewing landing pages, switching them on or off, and copying URLs.
          </p>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by keyword, path, or heading"
              className="w-full sm:flex-1 border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-blue-300"
            />

            <div className="text-sm text-gray-600 flex items-center whitespace-nowrap">
              Showing{" "}
              <span className="font-semibold text-gray-900">&nbsp;{items.length}&nbsp;</span>
              pages
              <span className="mx-2 text-gray-300">|</span>
              Live{" "}
              <span className="font-semibold text-gray-900">&nbsp;{enabledKeys.length}&nbsp;</span>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={enableTop10}
              className="border border-gray-200 px-4 py-2 rounded-full text-sm hover:bg-gray-50"
            >
              Enable top 10 (filtered)
            </button>
            <button
              type="button"
              onClick={enableFiltered}
              className="border border-gray-200 px-4 py-2 rounded-full text-sm hover:bg-gray-50"
            >
              Enable all (filtered)
            </button>
            <button
              type="button"
              onClick={disableFiltered}
              className="border border-gray-200 px-4 py-2 rounded-full text-sm hover:bg-gray-50"
            >
              Disable all (filtered)
            </button>
            <button
              type="button"
              onClick={disableAll}
              className="border border-red-200 text-red-700 px-4 py-2 rounded-full text-sm hover:bg-red-50"
            >
              Disable all pages
            </button>
          </div>

          <p className="text-xs text-gray-500 mt-3">
            Tip: search or filter, then use Enable top 10 to roll out in batches.
          </p>
        </section>

        <section className="max-w-5xl mx-auto">
          <div className="space-y-4">
            {items.map((p) => {
              const k = p.key
              const cardKey = p._safeKey
              const live = k ? isLive(k) : false
              const statusText = k ? (live ? "Live" : "Disabled") : "No key"
              const statusClass = !k ? "text-amber-700" : live ? "text-green-700" : "text-gray-500"

              return (
                <div
                  key={cardKey}
                  className="border border-gray-200 rounded-2xl p-5 bg-white shadow-sm"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-blue-700 mb-1">{p.group}</p>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {p.heroHeading || p.title}
                      </h2>
                      <p className="text-sm text-gray-600 mt-1 break-all">{p.path}</p>

                      <p className="text-xs font-semibold mt-2">
                        Status: <span className={statusClass}>{statusText}</span>
                        {k ? null : (
                          <span className="text-gray-400 font-normal">
                            {" "}
                            (add a unique <code>key</code> in landingPages.js)
                          </span>
                        )}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-3 items-center">
                      <Link
                        to={p.path}
                        className="inline-block bg-blue-600 text-white px-4 py-2 rounded-full hover:bg-blue-700 transition text-sm"
                      >
                        Open
                      </Link>

                      <button
                        type="button"
                        onClick={() => copyUrl(k || cardKey, p.path)}
                        className="inline-block border border-gray-200 text-gray-900 px-4 py-2 rounded-full hover:bg-gray-50 transition text-sm"
                      >
                        {copiedKey === (k || cardKey) ? "Copied" : "Copy URL"}
                      </button>

                      <button
                        type="button"
                        disabled={!k}
                        onClick={() => toggleLive(k)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                          !k
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : live
                              ? "bg-green-100 text-green-800 hover:bg-green-200"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                        title={!k ? "Missing key in landingPages.js" : undefined}
                      >
                        {!k ? "Needs key" : live ? "Turn off" : "Turn on"}
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-gray-600">
                      <div className="break-all">
                        <span className="font-semibold text-gray-800">Full URL:</span>{" "}
                        <span className="font-mono">{origin ? `${origin}${p.path}` : p.path}</span>
                      </div>
                      {k ? (
                        <div className="text-gray-500">
                          <span className="font-semibold text-gray-800">Key:</span>{" "}
                          <span className="font-mono">{k}</span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              )
            })}

            {items.length === 0 && (
              <div className="border border-gray-200 rounded-2xl p-6 bg-gray-50 text-gray-700">
                No matches.
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  )
}